import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, CircleAlert, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import { Badge, ConfidenceBreakdown } from "../lib/ui";
import ChainTrace from "../components/ChainTrace";
import ConformancePanel from "../components/ConformancePanel";
import RoleHomeShell from "../components/RoleHomeShell";
import StorySpine from "../components/StorySpine";
import SubFlowNav, { FocusPanel, MetricStrip } from "../components/SubFlow";
import { PRODUCT_IDENTITY, confidenceBreakdown, useAppGo } from "../lib/story";
import { canSignGates, navItemAllowed } from "../lib/master";
import { subflowHref, useSubFlow } from "../lib/subflow";
import { COCKPIT_DRILL, COCKPIT_FLOWS, COCKPIT_FOCUS_DETAIL } from "../lib/cockpitFlows";

export default function Cockpit({ app, portfolio, onSelectApp, user }) {
  const go = useAppGo(app?.id);
  const { focus } = useSubFlow("overview");
  if (!app) return null;
  const foundation = app.input_foundation || {};
  const readiness = foundation.readiness || {};
  const conf = confidenceBreakdown(app);
  const why = app.why || {};
  const before = app.before || {};
  const evidence = app.evidence || {};
  const counts = evidence.counts || {};
  const facts = evidence.facts || [];
  const value = app.value || {};
  const risks = app.risks || [];
  const alternatives = app.alternatives || [];
  const pain = before.pain || [];
  const assessments7c = app.assessments?.["7c"] || [];
  const assessments7s = app.assessments?.["7s"] || [];
  const mdr = (app.mdrs || []).filter(m => !m.superseded_by).slice(-1)[0];
  const authorized = mdr?.state === "AUTHORIZED";
  const guide = user?.guide || {};
  const maySign = canSignGates(guide);
  const mayDecide = navItemAllowed("/decision", guide);
  const mayDelivery = navItemAllowed("/delivery", guide);
  const mayAssess = navItemAllowed("/assessment", guide);
  const deployed = app.deployment?.state === "DEPLOYED" || app.deployment?.state === "SUCCESS";
  const g8Closed = app.deployment?.state === "SUCCESS";
  const coverageLabel = conf.components.find((c) => c.id === "coverage")?.value || `${evidence.coverage || 0}%`;
  const isOverview = focus === "overview";

  return (
    <div className="dashboard cockpit subflow-page" data-testid="cockpit-page">
      <div className="cockpit-selector">
        <label className="kicker" htmlFor="app-select">SELECT APPLICATION</label>
        <select id="app-select" data-testid="cockpit-app-select" value={app.id} onChange={e => onSelectApp(e.target.value)}>
          {(portfolio?.applications || []).map(a => (
            <option key={a.id} value={a.id}>{a.id} · {a.name} · {a.recommendation}</option>
          ))}
        </select>
      </div>

      <RoleHomeShell guide={guide} portfolio={portfolio} app={app} appId={app?.id} count={portfolio?.applications?.length} />

      {!isOverview && (
        <Link to={subflowHref("/", app.id, "overview")} className="analytics-back" data-testid="cockpit-back">
          <ChevronLeft size={16} /> Back to cockpit overview
        </Link>
      )}

      <FocusPanel
        kicker={`EXECUTIVE COCKPIT · ${app.id} · ${PRODUCT_IDENTITY}`}
        title={isOverview ? `${app.name} · ${app.recommendation}` : (COCKPIT_FLOWS.find(f => f.id === focus)?.label || "Cockpit")}
        detail={COCKPIT_FOCUS_DETAIL[focus] || app.recommendation_reason}
        badge={<Badge tone={authorized ? "green" : "amber"}>{authorized ? "MDR AUTHORIZED" : "REVIEW READY"}</Badge>}
        testId="cockpit-head"
      />

      {!isOverview && <SubFlowNav items={COCKPIT_FLOWS} focus={focus} appId={app.id} testId="cockpit-subflow" />}

      {isOverview && (
        <>
          <MetricStrip
            items={[
              { label: "7R", value: app.recommendation || "—", testId: "cockpit-kpi-7r" },
              { label: "Coverage", value: coverageLabel, testId: "cockpit-kpi-coverage" },
              { label: "Suitability", value: alternatives.find(a => a.recommended)?.suitability != null ? `${alternatives.find(a => a.recommended).suitability}%` : "—", testId: "cockpit-kpi-suit" },
              { label: "MDR", value: authorized ? "AUTHORIZED" : "PENDING", testId: "cockpit-kpi-mdr" },
            ]}
          />
          <StorySpine current="decide" compact />
          <ChainTrace app={app} guide={guide} onOpen={(route) => go(route)} testId="cockpit-chain-trace" />
          <ConformancePanel app={app} />
          <ConfidenceBreakdown breakdown={conf} />
          {navItemAllowed("/lifecycle", guide) && app.lifecycle && (
            <button type="button" className="lifecycle-rail" data-testid="cockpit-lifecycle-rail" onClick={() => go("/lifecycle")}>
              <small>GOVERNED PATH · {app.lifecycle.progress?.done || 0}/{app.lifecycle.progress?.total || 22}</small>
              <strong>Now: {(app.lifecycle.steps || []).find(s => s.id === app.lifecycle.current)?.label}</strong>
            </button>
          )}
          <div className="cockpit-actions reveal delay-2">
            {!authorized && maySign && mayDecide && (
              <button data-testid="cockpit-go-authorize" className="primary-button" onClick={() => go("/decision?focus=authorize")}>
                <ShieldCheck size={16} /> Authorize MDR
              </button>
            )}
            {authorized && !deployed && maySign && mayDelivery && (
              <button data-testid="cockpit-run-deployment" className="primary-button" onClick={() => go("/delivery")}>
                <Sparkles size={16} /> Open delivery
              </button>
            )}
            {mayDecide && <button data-testid="cockpit-open-decision" className="secondary-button" onClick={() => go("/decision")}>Open 7R decision <ArrowUpRight size={14} /></button>}
          </div>
          <section className="feature-panel analytics-panel" data-testid="cockpit-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {COCKPIT_DRILL.map(({ id, label }) => (
                <Link key={id} to={subflowHref("/", app.id, id)} className="lifecycle-phase-link" data-testid={`cockpit-drill-${id}`}>
                  <strong>{label}</strong>
                  <span>{COCKPIT_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {focus === "journey" && (
        <div className="bda-strip reveal delay-1">
          <div className="bda-item bda-before">
            <span className="bda-tag">BEFORE</span>
            <h3>Fragmented & risky</h3>
            <div className="bda-metrics">
              <div><small>Runtime</small><strong>{before.technology || "—"}</strong></div>
              <div><small>Release</small><strong>{before.release_cadence || "—"}</strong></div>
              <div><small>Availability</small><strong>{before.availability || "—"}</strong></div>
              <div><small>Monthly run cost</small><strong>{before.monthly_run_cost || "—"}</strong></div>
            </div>
            <ul>
              {pain.slice(0, 3).map(p => <li key={p}><CircleAlert size={13} /> {p}</li>)}
            </ul>
          </div>
          <div className="bda-arrow">→</div>
          <div className="bda-item bda-decision">
            <span className="bda-tag decision">DECISION</span>
            <h3>{app.recommendation}</h3>
            <div className="bda-metrics">
              <div><small>Coverage</small><strong>{coverageLabel}</strong></div>
              <div><small>Suitability</small><strong>{alternatives.find(a => a.recommended)?.suitability || "-"}%</strong></div>
              <div><small>MDR</small><strong>{mdr?.version || "—"}</strong></div>
            </div>
          </div>
          <div className="bda-arrow">→</div>
          <div className={`bda-item bda-after ${g8Closed ? "bda-after-live" : ""}`}>
            <span className="bda-tag after">AFTER</span>
            <h3>{g8Closed ? "Production success" : deployed ? "In hypercare" : "Forecast"}</h3>
            <div className="bda-metrics">
              <div><small>Target TCO</small><strong>{value.target_tco || "—"}</strong></div>
              <div><small>{g8Closed ? "Actual savings" : "Expected savings"}</small><strong>{(g8Closed ? value.actual_savings : value.expected_savings) || "—"}</strong></div>
              <div><small>ROI</small><strong>{g8Closed && value.actual_roi ? `${value.actual_roi}%` : `Forecast ${value.expected_roi ?? "—"}%`}</strong></div>
            </div>
          </div>
        </div>
      )}

      {focus === "reasoning" && (
        <div className="reasoning-drawer cockpit-reasoning" data-testid="cockpit-reasoning-drawer">
          <strong>Why this recommendation?</strong>
          <p className="muted">{app.recommendation_reason}</p>
          <div className="reasoning-columns">
            <div>
              <h4>Supporting evidence</h4>
              {facts.filter(f => f.state === "VERIFIED" || f.state === "KNOWN").slice(0, 4).map(f => (
                <div key={f.id}><Check size={13} /> {f.type}: {f.value}</div>
              ))}
            </div>
            <div>
              <h4>Constraints & unknowns</h4>
              {risks.map(r => <div key={r.title}><CircleAlert size={13} /> {r.title}</div>)}
            </div>
            <div>
              <h4>Why alternatives ranked lower</h4>
              {alternatives.filter(a => !a.recommended).slice(0, 3).map(a => (
                <div key={a.strategy}><ChevronRight size={13} /> {a.strategy}: {a.note}</div>
              ))}
            </div>
          </div>
          <div className="reasoning-footer"><LockKeyhole size={13} /> Explainable decision · AI is advisory · human authorization required.</div>
        </div>
      )}

      {focus === "evidence" && readiness && (
        <div className="cockpit-reasoning evidence-drawer" data-testid="cockpit-evidence-drawer">
          <strong>DECISION EVIDENCE — {app.id}</strong>
          <p className="muted">Coverage {readiness.coverage}%. Decision-critical unresolved: {readiness.critical_unresolved_count}.</p>
          <div className="evidence-domain-grid">
            {(foundation.domains || []).map(d => (
              <div key={d.id} data-testid={`cockpit-evidence-${d.id}`}>
                <small>{d.title.toUpperCase()}</small>
                <strong>{d.fact_count} facts</strong>
                <span>{d.counts?.verified || 0} verified / {d.counts?.unknown || 0} unknown</span>
              </div>
            ))}
          </div>
          <div className={`readiness-eligible ${readiness.eligible_for_authorization ? "yes" : "no"}`}>
            <ShieldCheck size={14} /> Eligible for human authorization: {readiness.eligible_for_authorization ? "YES" : "NO"}
          </div>
          {mayAssess && <button className="text-button" onClick={() => go("/assessment?focus=readiness")}>Open assessment readiness <ChevronRight size={14} /></button>}
        </div>
      )}

      {focus === "assessments" && (
        <section className="feature-panel assessments-panel" data-testid="cockpit-assessments">
          <div className="panel-header">
            <div>
              <p className="kicker">7C / 7S · FED BY ASSESSMENT INPUTS</p>
              <h2>Structured assessment</h2>
            </div>
            {mayAssess && <button type="button" className="secondary-button small-btn" onClick={() => go("/assessment?focus=lenses")}>View lenses</button>}
          </div>
          <div className="assessments-grid">
            <div className="assessments-col">
              <h4>7C</h4>
              {assessments7c.map(a => (
                <div className="assessment-row" key={a.lens} data-testid={`cockpit-7c-${a.lens.toLowerCase().replaceAll(" ", "-")}`}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                </div>
              ))}
            </div>
            <div className="assessments-col">
              <h4>7S</h4>
              {assessments7s.map(a => (
                <div className="assessment-row" key={a.lens} data-testid={`cockpit-7s-${a.lens.toLowerCase().replaceAll(" ", "-")}`}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar cyan" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {focus === "timeline" && (
        <div className="cockpit-lower reveal delay-3">
          <section className="feature-panel">
            <div className="panel-header">
              <div>
                <p className="kicker">GOVERNANCE TIMELINE · G1–G8</p>
                <h2>Deployment ≠ Production success</h2>
              </div>
              <Badge tone={g8Closed ? "green" : deployed ? "amber" : "cyan"}>{g8Closed ? "G8 PASS" : deployed ? "Hypercare" : "Governed"}</Badge>
            </div>
            <div className="gate-track">
              {(app.gates || []).map((g, i) => (
                <div key={g.id} data-testid={`cockpit-gate-${g.id.toLowerCase()}`} className={`gate-node ${g.state === "PASS" ? "done" : (g.state === "READY" || g.state === "IN_REVIEW" || g.state === "IN_HYPERCARE") ? "current" : ""}`}>
                  <span>{g.state === "PASS" ? <Check size={12} /> : g.id.slice(1)}</span>
                  <small>{g.id}</small>
                  <b>{g.name.split(" ")[0]}</b>
                  {i < 7 && <i />}
                </div>
              ))}
            </div>
          </section>
          <section className="feature-panel value-side">
            <div className="panel-header">
              <div>
                <p className="kicker">PROVE · EXPECTED VS ACTUAL</p>
                <h2>Connected back to WHY</h2>
              </div>
              <Badge tone="cyan">{why.driver || "—"}</Badge>
            </div>
            <div className="value-bars">
              <div><span>Baseline TCO</span><b style={{ width: "100%" }} /><strong>{value.baseline_tco || "—"}</strong></div>
              <div><span>Target TCO</span><b style={{ width: "57%" }} /><strong>{value.target_tco || "—"}</strong></div>
              <div><span>Actual TCO</span><b className={g8Closed ? "" : "actual-bar"} style={{ width: g8Closed ? "57%" : "3%" }} /><strong>{value.actual_tco || "—"}</strong></div>
            </div>
            <div className="roi-callout">
              <strong>{g8Closed && value.actual_roi ? `${value.actual_roi}%` : `Forecast ${value.expected_roi ?? "—"}%`}</strong>
              <span>{g8Closed ? "actual ROI" : "expected ROI"}</span>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
